import { IMap } from "@esdm/map/dist/map/IMap";
import { GazetteerBase } from "@esdm/map/dist/gazetteer/GazetteerBase";
import { GazetteerSearchType } from "@esdm/map/dist/gazetteer/GazetteerSearchType";
import { IGazetteerEntry } from "@esdm/map/dist/gazetteer/os/IGazetteerEntry";
import { IOSGazetteerResult } from "@esdm/map/dist/gazetteer/os/IOSGazetterResult";
import { IOsOpenNamesSearchParams } from "@esdm/map/dist/gazetteer/os/IOsOpenNamesSearchParams";
import { IMappingTextPack } from "@esdm/map/dist/resources/MappingTextPack";

import * as olProj from 'ol/proj';
import * as olExtent from 'ol/extent';
import Style from "ol/style/Style";
import TextStyle from "ol/style/Text";
import Fill from "ol/style/Fill";
import { Bounds } from "@esdm/map/dist/map/Bounds";
import { Extent } from "ol/extent";

interface IOsOpenSpaceResults {
    searchType: GazetteerSearchType;
    results: []
}

export class OsOpenNames extends GazetteerBase {

    private _osInitialised: boolean = false;
    private _currentSearchTerm: string = "";
    private _helpElement: JQuery<HTMLElement>;
    private _osNamesUrl: string = "https://api.os.uk/search/names/v1/find";
    private _mapBounds: Extent;

    constructor(
        _map: IMap,
        _apikey: string,
        _textPack: IMappingTextPack,          
        protected _zoomToLevel: number = 17,
        _elementId: string = "gazetteer",
        _btnCssClass: string = "btn btn-primary",
        _iconCssClass: string = "fa fa-search",
        _clearerCssClass: string = "fa fa-times"
    ) {
        super(_map, _textPack, _apikey, _elementId, _btnCssClass, _iconCssClass, _clearerCssClass);
        this.init();
    }

    private init = (): void => {
        if (!this._osInitialised) {
            
            this._mapBounds = olProj.transformExtent(this._map.options.extent, this._map.options.displayProjection, "EPSG:27700");                      

            //strip-loader should take this out for production
            console.log("Initialising OsOpenNames");

            //add extra div with help message
            //Street name searching takes account of the current map view. If you can't find what you're looking for in this list, try zooming out or moving the map to cover the area where the street is located.
            this._helpElement = $("<div id='OsOpenNames-help' class='mt-3' style='display:none'><p>Street name searching takes account of the current map view. If you can't find what you're looking for in this list, try zooming out or moving the map to cover the area where the street is located.</p></div>")
                .insertAfter(this.resultsSelect);

            let self = this;
            $(".clearer").on("click", function () {
                self._helpElement.hide();
            });

            this.locationPinStyle = new Style({
                text: new TextStyle({
                    text: '\uf3c5',
                    font: 'normal 52px "Font Awesome 6 Pro"',
                    textBaseline: 'bottom',
                    fill: new Fill({
                        color: '#ff0000'
                    })
                })
            });

            this._osInitialised = true;
        }
    }

    protected find = (searchTerm: string): void => {
        let searchType: GazetteerSearchType = this.getSearchType(searchTerm);

        if (searchTerm.trim() === "") {
            return;
        }

        this._currentSearchTerm = searchTerm;

        //remove all existing entries      
        this.resultsSelect.empty();
        this.resultsSelect.hide();
        this._helpElement.hide();
        this.noResults.hide();
        this.pointLayer.getSource().clear();

        switch (searchType) {
            case GazetteerSearchType.Postcode:
                this.findPostcode(searchTerm);
                break;

            case GazetteerSearchType.Location:
                this.findAddressAndLocation(searchTerm);
                break;
        }
    }

    private findPostcode = (searchTerm: string): void => {
        let self = this;
        let error = function (error) {
            self.loadingIndicator.hide();
            console.error(error);
        }
        let success = function (result: any) {
            let osResult: IOsOpenSpaceResults = {
                searchType: GazetteerSearchType.Postcode,
                results: result.results
            };
            self.onSearchGazetteerResult(osResult);
        }

        let search = this.searchGazetteer({
            url: this._osNamesUrl,
            //key: "1D5aW6pbptU2bufc2DBAux8JYvtrbeon",
            key: self._apikey,
            query: searchTerm,
            maxResults: 20,
            fq: "LOCAL_TYPE:Postcode"
        });
        $.when(search)
            .then(success, error)
            .always(function () { self.loadingIndicator.hide(); });
    }

    private findAddressAndLocation = (searchTerm: string): void => {
        let self = this;

        let combinedResult: IOsOpenSpaceResults = {
            searchType: GazetteerSearchType.LocationAndAddressLine,
            results: []
        };

        let handleResults = function (locationResult: any, addressResult: any) {
            let res = <any>[];
            
            if (locationResult &&  locationResult.results && locationResult.results.length > 0) {
                res = res.concat(locationResult.results);
            }
            
            if (addressResult && addressResult.results && addressResult.results.length > 0) {
                res = res.concat(addressResult.results);
            }

            combinedResult.results = res;
            self.onSearchGazetteerResult(combinedResult);
        }

        let error = function () {
            self.loadingIndicator.hide();
        }

        $.when(
            this.findLocation(searchTerm).then(
                function(result) { return result; },
                function(err) { 
                    console.error("findLocation error: ",err);
                    return $.Deferred().resolve(null); 
                }
            ),
            this.findAddress(searchTerm).then(
                function(result) { return result; },
                function(err) { 
                    console.error("findAddress error:", err);
                    return $.Deferred().resolve(null); 
                }
            )
        )
        .then(handleResults, error)
        .always(function () { self.loadingIndicator.hide(); });
    }

    private findLocation = (searchTerm: string): JQuery.jqXHR<any> => {
        return this.searchGazetteer({
            url: this._osNamesUrl,
            //key: "1D5aW6pbptU2bufc2DBAux8JYvtrbeon",
            key: this._apikey,
            query: searchTerm,
            maxResults: 10,
            fq: "LOCAL_TYPE:City LOCAL_TYPE:Town LOCAL_TYPE:Hamlet LOCAL_TYPE:Other_Settlement LOCAL_TYPE:Village LOCAL_TYPE:Suburban_Area"
        });
    }

    private findAddress = (searchTerm: string): JQuery.jqXHR<any> => {        
        let mapExtent = this._map.getReprojectedMapExtent("EPSG:27700");
        let bounds = [mapExtent.minx.toFixed(2), mapExtent.miny.toFixed(2), mapExtent.maxx.toFixed(2), mapExtent.maxy.toFixed(2)];

        return this.searchGazetteer({
            url: this._osNamesUrl,
            //key: "1D5aW6pbptU2bufc2DBAux8JYvtrbeon",
            key: this._apikey,
            query: searchTerm,
            maxResults: 10,
            fq: "LOCAL_TYPE:Named_Road",
            bounds: bounds.join(",")
        });
    }

    private searchGazetteer = (params: IOsOpenNamesSearchParams): JQuery.jqXHR<any> => {
        let self = this;
        let url = params.url;
        delete params.url;

        self.loadingIndicator.show();

        return $.ajax({
            url: url,
            data: params,
            dataType: "json"
        });
    }

    private onSearchGazetteerResult = (e: IOsOpenSpaceResults): void => {
        if (e.results && e.results.length > 0) {
            //one or more matches
            this.processSearchResult(e, this._zoomToLevel);

        } else {
            this.resultsSelect.empty();
            this.resultsSelect.append(`<option>${this._textPack.noResultsFound}</option>`);
            this.noResults.show();
        }
    }

    private processSearchResult = (
        osResults: IOsOpenSpaceResults,
        zoomToLevel: number
    ): void => {
        let self = this;
        let locations = new Array<IOSGazetteerResult>(),
            location: IOSGazetteerResult;

        //remove all existing entries      
        self.resultsSelect.empty();
        self.resultsSelect.hide();
        self.noResults.hide();

        let mapExtent = self._map.options.extent;

        let getDescOrValue = function (g: IGazetteerEntry) {
            return (g.LOCAL_TYPE === "Postcode") ? g.NAME1 : `${g.NAME1}, ${(g.POPULATED_PLACE || g.DISTRICT_BOROUGH || g.COUNTY_UNITARY)}`;
        };
        let getResultType = function (g: IGazetteerEntry): GazetteerSearchType {
            return (g.LOCAL_TYPE === "Postcode") ? GazetteerSearchType.Postcode : (g.LOCAL_TYPE === "Named Road") ? GazetteerSearchType.AddressLine : GazetteerSearchType.Location;
        }
        let getDistance = function (g: IGazetteerEntry) {
            let coord = olProj.toLonLat([g.GEOMETRY_X, g.GEOMETRY_Y], "EPSG:27700");
            return self.getDistanceFromMapCenter([coord[0], coord[1]]);
        }

        //create the final results array
        for (let i = 0; i < osResults.results.length; i++) {
            let gazEntry = <IGazetteerEntry>osResults.results[i]["GAZETTEER_ENTRY"];

            let coord = olProj.transform([gazEntry.GEOMETRY_X, gazEntry.GEOMETRY_Y], "EPSG:27700", self.displayProjection);
            if (olExtent.containsCoordinate(mapExtent, coord) === true) {
                location = {
                    Easting: coord[0],
                    Northing: coord[1],
                    Name: getDescOrValue(gazEntry),
                    Value: getDescOrValue(gazEntry),
                    ZoomLevel: zoomToLevel,
                    ResultType: getResultType(gazEntry),
                    DistanceInMetres: getDistance(gazEntry)
                };
                locations.push(location);
            }
        }

        //sort by distance
        locations = locations.sort(function (a: IOSGazetteerResult, b: IOSGazetteerResult) {
            return (a.DistanceInMetres < b.DistanceInMetres) ? -1 : (a.DistanceInMetres > b.DistanceInMetres) ? 1 : 0;
        });

        //now add the distance to the name
        locations.forEach((location) => {
            if (location.DistanceInMetres > 1000) {
                location.Name += ` (${(Math.round(location.DistanceInMetres / 1000 * 100) / 100).toFixed(0)} km)`;
            } else if (location.DistanceInMetres > 200) {
                location.Name += ` (${(Math.round(location.DistanceInMetres * 100) / 100).toFixed(0)}m)`;
            }
        });

        self.searchResults = new Array<IOSGazetteerResult>();

        if (locations.length === 0) {
            self.resultsSelect.append(`<option>${this._textPack.noResultsFound}</option>`);
            self.noResults.show();

        } else if (locations.length > 1) {

            //add empty option
            self.resultsSelect.append(`<option>${this._textPack.selectFromResults}</option>`);  

            if (osResults.searchType === GazetteerSearchType.LocationAndAddressLine) {
                //define 2 opts groups
                let optStreet = $("<optgroup label='Road/street'></optgroup");
                let optLocation = $("<optgroup label='Location'></optgroup");

                for (let i = 0; i < locations.length; i++) {
                    if (locations[i].ResultType === GazetteerSearchType.AddressLine) {
                        optStreet.append(`<option data-result-index="${i}">${locations[i].Name}</option>`);
                    } else {
                        optLocation.append(`<option data-result-index="${i}">${locations[i].Name}</option>`);
                    }

                    if (optLocation.children("option").length > 0) {
                        self.resultsSelect.append(optLocation);
                    }
                    if (optStreet.children("option").length > 0) {
                        self.resultsSelect.append(optStreet);
                    }
                    self.searchResults.push(locations[i]);
                }

            } else {
                //postcode               
                for (let i = 0; i < locations.length; i++) {

                    if (locations[i].Value.replace(/ /g, '').toLowerCase() === this._currentSearchTerm.replace(/ /g, '').toLowerCase()) {
                        //if the postcode matches exactly then go straight to it                      
                        self.addPinAndCenterMap([locations[i].Easting, locations[i].Northing], zoomToLevel);
                        return;
                    }

                    self.resultsSelect.append(`<option data-result-index="${i}">${locations[i].Name}</option>`);
                    self.searchResults.push(locations[i]);
                }
            }
            self.resultsSelect.show();
            self._helpElement.show();
        }
        else if (locations.length === 1) {
            self.addPinAndCenterMap([locations[0].Easting, locations[0].Northing], zoomToLevel);
        }
    }

    private getSearchType = (searchTerm: string): GazetteerSearchType => {
        //If the search term has numeric characters then assume it's postcode
        return (/\d/.test(searchTerm)) ? GazetteerSearchType.Postcode : GazetteerSearchType.Location;
    }
}